import React, { useState, useEffect, useRef } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl } from '../../lib/ep';

import { MainButton, SecondaryButton, SquaredButton } from './ActionButton';
import ActionLink from './ActionLink';
import ButtonCollection from './ButtonCollection';

/**
 * Hook that alerts clicks outside of the passed ref
 */
function useOutsideAlerter(enabled, ref, cb) {
	useEffect(() => {
		const handleClickOutside = (event) => {
			if(enabled && ref.current && !ref.current.contains(event.target)) {
				if(cb != undefined) cb();
				document.removeEventListener("mousedown", handleClickOutside);
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [enabled, ref]);
}

export const DeleteButton = injectIntl(({ icon, ...props }) => {
	const disabled = props?.disabled ?? false;

	return (
		<ConfirmButton
			icon={(icon != undefined) ? icon : 'knk-delete'}
			color={(disabled) ? 'gray' : 'red'}
			{...props}
		/>
	)
});

const ConfirmButton = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [confirm, setConfirm] = useState(false);

	let classname = props?.classname ?? 'gui_confirm_this blackBg whiteColor';
	let classes = classname.split(" ");
	classes.push(`in_${props?._position ?? 'bottom'}`);
	classes.push(`is_${props?._distance_to_cta ?? 'normal'}`);
	classname = classes.join(" ");

	const disabled = props?.disabled ?? false;

	const confirmOnClick = (e, cb, conf) => {
		if(disabled) return;

		if(!confirm) {
			setConfirm(true);
			return;
		}

		setConfirm(false);

		if(!conf) {
			if(cb != undefined) cb();
			return;
		}

		if(props.onClick != undefined) props.onClick(e, cb);
		if(props._href != undefined) {
			const target = props?._target ?? '_self';
			if(target == '_self') {
				self.location.href = props._href;
			} else {
				window.location.href = props._href;
			}
		}
	};

	var Component = MainButton;
	if(props.type != undefined) {
		switch(props.type) {
			case 'link':
				Component = ActionLink;
				break;

			case 'secondary':
				Component = SecondaryButton;
				break;

			case 'save':
				// USE <SaveButton type="confirm"> INSTEAD
				break;
		}
	}

	const autotranslate_msg = props?.autotranslate_confirmMessage ?? true;

	const button_props = Object.assign({}, { ...props }, { onClick:confirmOnClick, _href:null, _target:null });
	let confirmMessage = props?.confirmMessage ?? false;
	if(confirmMessage !== false && autotranslate_msg) {
		confirmMessage = i18n(props.confirmMessage);
	}

	let main_classes = ['in_top', 'gui_confirm_button', 'gui_pos_rel'];
	if(props.main_classname != undefined) main_classes.push(props.main_classname);

	const wrapperRef = useRef(null);
	useOutsideAlerter(confirm, wrapperRef, () => { setConfirm(false); });

	return (
		<div class={main_classes.join(" ")} ref={wrapperRef}>
			<Component {...button_props}>{props.children}</Component>

			{(confirm) && (
				<div class={classname}>
					{(confirmMessage) && (
						<div class="additional_message" dangerouslySetInnerHTML={{ __html:confirmMessage.replace(/\n/g, '<br />') }}></div>
					)}

					<div class="confirm_content">
						<span>{i18n("Are you sure?")}</span>

						<ButtonCollection>
							<SquaredButton
								onClick={(e, cb) => confirmOnClick(e, cb, true)}
								color={false}
								icon='knk-on greenButton'
								aria-label="Yes"
							/>

							<SquaredButton
								onClick={(e, cb) => confirmOnClick(e, cb, false)}
								color={false}
								icon='knk-off redButton'
								aria-label="No"
							/>
						</ButtonCollection>
					</div>
				</div>
			)}
		</div>
	);
}

export default injectIntl(ConfirmButton);